import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CLUSTERS, MAX_TOKENS, PRESETS, type HeadId, type StageId, type Vec3 } from './data/content';
import { attentionWeights, candidatesFor, embeddingFor, nearestWords, normalizeWord, sampleIndex, softmax, tokenColor, tokenIdFor, wordInfo } from './lib/model';
import { BARS_BASE, BLANK_W, SPREAD_GAP, TIGHT_GAP, barHeight, barX, cloudToWorld, rowLayout, tileWidth } from './lib/layout';
import { CHAPTER_SPANS, LAND_MS, TIMELINE, TOTAL_MS, beatAt, shotAt } from './lib/timeline';
import { useMediaQuery } from './lib/hooks';
import { canUseWebGL } from './lib/webgl';
import { Scene } from './scene/Scene';
import { SceneBoundary } from './scene/SceneBoundary';
import type { SceneToken } from './scene/TokenObject';
import { Captions } from './ui/Captions';
import { ChapterCard } from './ui/ChapterCard';
import { ChatScene } from './ui/ChatScene';
import { ControlBar } from './ui/ControlBar';
import { EndCard } from './ui/EndCard';
import { ExploreHud } from './ui/ExploreHud';
import { FlatScene } from './ui/FlatScene';
import { PosterCard } from './ui/PosterCard';
import { SentenceBar } from './ui/SentenceBar';
import { TitleMark } from './ui/TitleMark';
type Mode = 'poster' | 'film' | 'end';
type RawToken = {
  key: string;
  text: string;
  id: number;
  spawnFrom?: Vec3;
};
const IDLE_MS = 2500;
const CHAPTER_CARD_MS = 2600;
const CHAT_EXIT_MS = 1500;
const TICK_MS = 100;
const BLANK_STAGES: StageId[] = ['tokens', 'embeddings', 'attention', 'layers', 'prediction'];
// The last moment the 3D scene is in view before the epilogue brings the chat back.
const LAST_SCENE_MS = ([...TIMELINE].reverse().find(b => !b.chat)?.end ?? TOTAL_MS) - 1;
export function App() {
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const wide = useMediaQuery('(min-width: 900px)');
  const [presetId, setPresetId] = useState(PRESETS[0].id);
  const [mode, setMode] = useState<Mode>('poster');
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const timeRef = useRef(0);
  const [captionsOn, setCaptionsOn] = useState(true);
  const [idle, setIdle] = useState(false);
  const [viewKey, setViewKey] = useState(0);

  // The viewer's own changes while paused. Playing or seeking clears them.
  const [manual, setManual] = useState<RawToken[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [headOverride, setHeadOverride] = useState<HeadId | null>(null);
  const [tempOverride, setTempOverride] = useState<number | null>(null);
  const [manualPick, setManualPick] = useState<number | null>(null);
  const [manualLanding, setManualLanding] = useState(false);
  const [webglOk] = useState(canUseWebGL);
  const [sceneFailure, setSceneFailure] = useState<string | null>(webglOk ? null : 'WebGL is not available in this browser.');
  const [sceneKey, setSceneKey] = useState(0);
  const recentLosses = useRef<number[]>([]);
  const preset = PRESETS.find(p => p.id === presetId) ?? PRESETS[0];
  const shot = shotAt(time);
  const head = headOverride ?? shot.head;
  const temperature = tempOverride ?? shot.temperature;

  // The film always takes the most likely token, so every viewing tells the same story.
  const scripted = useMemo(() => {
    const texts = preset.tokens.map(([text]) => text);
    return TIMELINE.filter(b => b.sample).map((_, i): RawToken => {
      const candidates = candidatesFor(texts);
      const probs = softmax(candidates.map(c => c.logit), 1);
      const top = candidates[0];
      texts.push(top.text);
      return {
        key: `${preset.id}-film-${i}`,
        text: top.text,
        id: tokenIdFor(top.text),
        spawnFrom: [barX(0, candidates.length), BARS_BASE + barHeight(probs[0]) + 0.3, 0]
      };
    });
  }, [preset]);
  const tokens = useMemo<SceneToken[]>(() => {
    const raw: RawToken[] = [...preset.tokens.map(([text, id], i) => ({
      key: `${preset.id}-${i}`,
      text,
      id
    })), ...scripted.slice(0, shot.samples), ...manual];
    const seen = new Map<string, number>();
    return raw.map((t, i) => {
      const info = wordInfo(t.text);
      const dup = seen.get(info.norm) ?? 0;
      seen.set(info.norm, dup + 1);
      return {
        key: t.key,
        text: t.text,
        id: t.id,
        color: tokenColor(i),
        vector: embeddingFor(t.text),
        cloudPos: cloudToWorld([info.pos[0], info.pos[1] + dup * 0.6, info.pos[2]]),
        spawnFrom: t.spawnFrom
      };
    });
  }, [preset, scripted, shot.samples, manual]);
  const texts = useMemo(() => tokens.map(t => t.text), [tokens]);
  const selectedIndex = Math.min(selected ?? tokens.length - 1, tokens.length - 1);
  const widths = useMemo(() => texts.map(tileWidth), [texts]);
  const tight = useMemo(() => rowLayout(widths, TIGHT_GAP), [widths]);
  const spread = useMemo(() => rowLayout(widths, SPREAD_GAP), [widths]);
  const attention = useMemo(() => attentionWeights(texts, selectedIndex, head), [texts, selectedIndex, head]);
  const rawCandidates = useMemo(() => candidatesFor(texts), [texts]);
  const probs = useMemo(() => softmax(rawCandidates.map(c => c.logit), temperature), [rawCandidates, temperature]);
  const candidates = rawCandidates.map((c, k) => ({
    text: c.text,
    prob: probs[k]
  }));
  const selectedText = texts[selectedIndex];
  const neighbors = useMemo(() => nearestWords(selectedText, 3), [selectedText]);
  const selectedCluster = wordInfo(selectedText).cluster;
  const clusterLabel = CLUSTERS.find(c => c.id === selectedCluster)?.label ?? 'Grammar';
  const hiddenWords = useMemo(() => new Set(texts.map(normalizeWord)), [texts]);
  const atCap = tokens.length >= MAX_TOKENS;
  const picking = manualPick ?? (shot.picking ? 0 : null);
  useEffect(() => {
    if (!playing) return;
    let last = performance.now();
    const id = window.setInterval(() => {
      const now = performance.now();
      const next = Math.min(TOTAL_MS, timeRef.current + (now - last));
      last = now;
      timeRef.current = next;
      setTime(next);
      if (next >= TOTAL_MS) {
        setPlaying(false);
        setMode('end');
      }
    }, TICK_MS);
    return () => window.clearInterval(id);
  }, [playing]);
  useEffect(() => {
    let timer = 0;
    const wake = () => {
      setIdle(false);
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setIdle(true), IDLE_MS);
    };
    wake();
    window.addEventListener('pointermove', wake);
    window.addEventListener('keydown', wake);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('pointermove', wake);
      window.removeEventListener('keydown', wake);
    };
  }, []);
  function clearViewerChanges() {
    setManual([]);
    setSelected(null);
    setHeadOverride(null);
    setTempOverride(null);
    setManualPick(null);
  }
  function moveTo(ms: number) {
    const t = Math.min(TOTAL_MS, Math.max(0, ms));
    timeRef.current = t;
    setTime(t);
    clearViewerChanges();
  }
  function seek(ms: number) {
    moveTo(ms);
    setMode('film');
  }
  function play() {
    if (timeRef.current >= TOTAL_MS) moveTo(0);else clearViewerChanges();
    setMode('film');
    setPlaying(true);
    setViewKey(k => k + 1);
  }
  function togglePlay() {
    if (playing) setPlaying(false);else play();
  }
  function step(dir: -1 | 1) {
    const beat = beatAt(timeRef.current);
    if (dir > 0) {
      seek(TIMELINE[beat.index + 1]?.start ?? TOTAL_MS);
      return;
    }
    const intoBeat = timeRef.current - beat.start;
    seek(intoBeat > 1200 ? beat.start : TIMELINE[Math.max(0, beat.index - 1)].start);
  }
  function jumpToChapter(chapter: number) {
    moveTo(CHAPTER_SPANS[chapter].start);
    setMode('film');
    setPlaying(true);
    setViewKey(k => k + 1);
  }
  function choosePreset(id: string) {
    setPresetId(id);
    moveTo(0);
    if (mode !== 'poster') {
      setMode('film');
      setPlaying(true);
      setViewKey(k => k + 1);
    }
  }
  const pauseToExplore = useCallback(() => setPlaying(false), []);
  function selectToken(index: number) {
    setSelected(index);
    pauseToExplore();
  }
  function sample() {
    if (manualPick !== null || atCap) return;
    const k = sampleIndex(probs);
    const choice = rawCandidates[k];
    const spawnFrom: Vec3 = [barX(k, rawCandidates.length), BARS_BASE + barHeight(probs[k]) + 0.3, 0];
    setManualPick(k);
    window.setTimeout(() => {
      setManual(g => [...g, {
        key: `gen-${Date.now()}`,
        text: choice.text,
        id: tokenIdFor(choice.text),
        spawnFrom
      }]);
      setSelected(null);
      setManualPick(null);
      setManualLanding(true);
      window.setTimeout(() => setManualLanding(false), LAND_MS);
    }, reducedMotion ? 0 : 520);
  }
  function startOver() {
    setManual([]);
    setSelected(null);
    setManualPick(null);
  }
  const keys = useRef({
    togglePlay,
    step
  });
  keys.current = {
    togglePlay,
    step
  };
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === ' ' && tag !== 'BUTTON' && tag !== 'SUMMARY') {
        e.preventDefault();
        keys.current.togglePlay();
      }
      if (e.key === 'ArrowRight') keys.current.step(1);
      if (e.key === 'ArrowLeft') keys.current.step(-1);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  function failScene(reason: string) {
    setSceneFailure(current => current ?? reason);
  }
  function retryScene() {
    recentLosses.current = [];
    setSceneFailure(null);
    setSceneKey(k => k + 1);
  }

  // A fresh canvas gets a fresh context, which evicts the oldest one elsewhere
  // rather than this view. Give up only when losses keep coming.
  const recoverScene = useCallback(() => {
    const now = Date.now();
    recentLosses.current = [...recentLosses.current.filter(t => now - t < 30_000), now];
    if (recentLosses.current.length > 3) {
      setSceneFailure(current => current ?? 'The 3D view keeps losing its graphics context. Too many 3D views open at once, including in other tabs, can cause this.');
      return;
    }
    setSceneKey(k => k + 1);
  }, []);
  const chapter = shot.beat.chapter;
  const showChapterCard = mode === 'film' && playing && chapter > 0 && time - CHAPTER_SPANS[chapter].start < CHAPTER_CARD_MS;
  const chromeVisible = mode !== 'film' || !playing || !idle;
  const chat = mode === 'film' && shot.chat && (shot.chat !== 'enter' || shot.elapsed < CHAT_EXIT_MS) ? shot.chat : null;
  // While a pick is in the air the blank waits where it will land, then moves on.
  const landing = shot.landing || manualLanding;
  const blank = {
    x: landing ? spread.xs[spread.xs.length - 1] : spread.total / 2 + SPREAD_GAP + BLANK_W / 2,
    visible: shot.blank && BLANK_STAGES.includes(shot.stage)
  };
  function exploreFromStart() {
    seek(CHAPTER_SPANS[1].start);
    setPlaying(false);
  }
  function exploreFromEnd() {
    seek(LAST_SCENE_MS);
    setPlaying(false);
  }
  return <div className="relative h-screen w-full overflow-hidden bg-[#07080C] font-sans text-[#EDEEF2]" data-id="element-0" data-mp="opqo183rlx14">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_60%_at_50%_42%,#10131D_0%,#07080C_70%)]" data-id="element-1" data-mp="zw9mm3ss0er" />
      <div className="absolute inset-0" data-id="element-2" data-mp="3s80nygdxejn2">
        {sceneFailure ? <FlatScene tokens={tokens} neutral={shot.stage === 'text'} wide={wide} reason={sceneFailure} onRetry={webglOk ? retryScene : undefined} data-id="element-3" data-mp="a149v9pvjbbh" /> : <SceneBoundary key={sceneKey} onError={failScene} data-id="element-4" data-mp="1lf7zk3pfidaq">
            <Scene stage={shot.stage} tokens={tokens} widths={widths} tightXs={tight.xs} spreadXs={spread.xs} rowWidth={spread.total} selectedIndex={selectedIndex} onSelect={selectToken} head={head} attention={attention} hiddenWords={hiddenWords} neighbors={neighbors} focusCluster={selectedCluster} candidates={candidates} picking={picking} viewKey={viewKey} wide={wide} reducedMotion={reducedMotion} drift={mode === 'film' && playing} onUserInteract={pauseToExplore} panelOpen={mode === 'film' && !playing} blank={blank} framing={shot.framing} onContextLost={recoverScene} data-id="element-5" data-mp="1gholb3sqe0is" />
          </SceneBoundary>}
      </div>

      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-64 bg-[linear-gradient(to_top,#07080C_0%,rgba(7,8,12,0.82)_38%,rgba(7,8,12,0)_100%)]" data-id="element-6" data-mp="37susk2cskk0r" />

      <TitleMark visible={mode === 'film' && chromeVisible} data-id="element-7" data-mp="3596k2p30n3r2" />
      {chat ? <ChatScene phase={chat} progress={shot.progress} message={preset.userMessage} reply={texts.join('')} data-id="element-8" data-mp="30b4zu0tusye0" /> : <SentenceBar tokens={tokens} promptLength={preset.tokens.length} selectedIndex={selectedIndex} onSelect={selectToken} showTokens={shot.stage !== 'text'} wide={wide} data-id="element-9" data-mp="2raqckvxbx97v" />}

      {showChapterCard && <ChapterCard key={chapter} chapter={chapter} data-id="element-10" data-mp="2hjlzk1r7ryfo" />}
      {mode === 'film' && captionsOn && <Captions text={shot.beat.caption} beatKey={shot.beat.index} data-id="element-11" data-mp="2cm3mq0a2sz2e" />}

      {mode === 'film' && !playing && <ExploreHud wide={wide} controls={{
      stage: shot.stage,
      tokens,
      selectedIndex,
      onSelect: setSelected,
      neighbors,
      clusterLabel,
      head,
      onHead: setHeadOverride,
      attention,
      candidates,
      temperature,
      onTemperature: setTempOverride,
      onSample: sample,
      onStartOver: startOver,
      atCap,
      picking,
      hasGenerated: manual.length > 0
    }} data-id="element-12" data-mp="27kwigb756i0h" />}

      {mode !== 'poster' && <ControlBar time={time} playing={playing} visible={chromeVisible} onTogglePlay={togglePlay} onSeek={seek} onStep={step} presetId={preset.id} onPreset={choosePreset} captionsOn={captionsOn} onToggleCaptions={() => setCaptionsOn(on => !on)} onResetView={() => setViewKey(k => k + 1)} data-id="element-13" data-mp="3f0kzitxye0dr" />}

      {mode === 'poster' && <PosterCard presetId={preset.id} onPreset={choosePreset} onPlay={play} onExplore={exploreFromStart} data-id="element-14" data-mp="148t7r13di5c3" />}
      {mode === 'end' && <EndCard onReplay={() => {
      moveTo(0);
      play();
    }} onExplore={exploreFromEnd} onChapter={jumpToChapter} data-id="element-15" data-mp="1jbtb3zb3h6ad" />}
    </div>;
}